// Multi-field sort editor for a tile's query. Shared by BOTH surfaces that let
// someone change a tile's ordering, so they can never drift apart:
//   • the tile editor  (editor/TileEditorPanel.jsx → Data section)
//   • Inspect query    (InspectQueryModal.jsx → alongside fields/filters/limit)
// It was originally built only into the tile editor, which is why the reporter
// on ticket 30f7fa1e couldn't find it — inspect is where they were looking.
//
// Sorts live on the query as an array of Looker sort strings in PRIORITY order:
// the first is the primary sort, so e.g. putting "start year" first groups every
// event in the same year together before a secondary sort orders within each
// group. Add fields, flip asc/desc, reorder priority, remove any sort. Reuses
// the tile's existing query.sorts schema, so a saved sort matches any other tile
// edit and round-trips to Looker unchanged.

// Parse a Looker sort string ("view.field desc") into { field, dir }. Field
// names never contain spaces, so a trailing " asc"/" desc" is the direction;
// anything else defaults to ascending (Looker's implicit default).
export function parseSort(s) {
  const str = String(s).trim();
  const m = str.match(/\s+(asc|desc)$/i);
  return m ? { field: str.slice(0, m.index).trim(), dir: m[1].toLowerCase() } : { field: str, dir: 'asc' };
}

// Serialise back to Looker sort strings. null when empty, so a cleared sort
// removes the key rather than baking an empty array into the query.
export function serialiseSorts(list) {
  return list.length ? list.map((s) => `${s.field} ${s.dir}`) : null;
}

export default function SortEditor({ sorts: rawSorts, fields, onChange, label = 'Sort by', style: styleOverrides }) {
  // Only surface sorts whose field is still selected (a pruned field can leave a
  // stale sort string behind); those are ignored here and cleaned on next commit.
  const sorts = (rawSorts || []).map(parseSort).filter((s) => fields.includes(s.field));
  const used = new Set(sorts.map((s) => s.field));
  const available = fields.filter((f) => !used.has(f));

  const commit = (next) => onChange(serialiseSorts(next));
  const setField = (i, field) => commit(sorts.map((s, j) => (j === i ? { ...s, field } : s)));
  const setDir = (i, dir) => commit(sorts.map((s, j) => (j === i ? { ...s, dir } : s)));
  const remove = (i) => commit(sorts.filter((_, j) => j !== i));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= sorts.length) return;
    const n = sorts.slice();
    [n[i], n[j]] = [n[j], n[i]];
    commit(n);
  };
  const add = () => { if (available.length) commit([...sorts, { field: available[0], dir: 'asc' }]); };

  return (
    <div style={styleOverrides}>
      {label ? <div style={labelStyle}>{label}</div> : null}
      {sorts.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>No sort — rows come back in the query&apos;s default order.</div>
      )}
      {sorts.map((s, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
          {sorts.length > 1 && (
            <span
              title={i === 0 ? 'Primary sort — groups rows by this field first' : `Sort priority ${i + 1}`}
              style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', minWidth: 14, textAlign: 'center' }}
            >{i + 1}</span>
          )}
          <select style={{ ...input, flex: 1, minWidth: 120 }} value={s.field} onChange={(e) => setField(i, e.target.value)}>
            {/* this row's field, plus any not already used on another row */}
            {[s.field, ...available].map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <select style={{ ...input, width: 88 }} value={s.dir} onChange={(e) => setDir(i, e.target.value)}>
            <option value="asc">Asc</option>
            <option value="desc">Desc</option>
          </select>
          {sorts.length > 1 && (
            <>
              <SortIconBtn title="Move up (higher priority)" disabled={i === 0} onClick={() => move(i, -1)}>▲</SortIconBtn>
              <SortIconBtn title="Move down (lower priority)" disabled={i === sorts.length - 1} onClick={() => move(i, 1)}>▼</SortIconBtn>
            </>
          )}
          <SortIconBtn title="Remove this sort field" onClick={() => remove(i)} danger>✕</SortIconBtn>
        </div>
      ))}
      {available.length > 0 && (
        <button type="button" onClick={add} style={addSortBtn}>＋ Add sort field</button>
      )}
    </div>
  );
}

function SortIconBtn({ children, onClick, title, disabled, danger }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        border: '1px solid var(--hairline)', background: 'var(--card)',
        cursor: disabled ? 'default' : 'pointer',
        color: disabled ? 'var(--hairline)' : danger ? 'var(--error)' : 'var(--muted)',
        borderRadius: 6, minWidth: 34, height: 34, fontSize: 12, lineHeight: 1, padding: 0, flexShrink: 0,
      }}
    >{children}</button>
  );
}

// Mobile-first: 40px-tall controls, rows wrap rather than overflow.
const input = { padding: '9px 10px', minHeight: 36, borderRadius: 8, border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', fontSize: 12.5, boxSizing: 'border-box' };
const labelStyle = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)', margin: '0 0 6px' };
const addSortBtn = { marginTop: 2, border: '1px dashed var(--hairline)', background: 'transparent', color: 'var(--brand)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, borderRadius: 6, padding: '8px 12px', minHeight: 36 };
